/**
 * Nachweisliste auf der Seite "Nachweise" eines Antrags – eine Kachel je Stand (laufend, offen,
 * freigegeben, archiviert). Ein Klick auf eine Zeile öffnet das Detail des Nachweises; das
 * steht dann allein auf der Seite (`EvidenceDetail`).
 *
 * Verantwortlicher und nächster Termin haben eigene Spalten und sind direkt in der Zeile
 * änderbar.
 *
 * Den Status ändert man über Aktionen (ADR 007): Der Knopf am Zeilenende führt den nächsten
 * Schritt aus, ein Klick auf den Status-Chip öffnet alle zulässigen Aktionen samt „Archivieren". Archivierte
 * Nachweise sind ausgeblendet, bis man sie über den Schalter im Kopf zurückholt.
 */
import React, { useMemo, useCallback, useState } from "react";
import { Typography, Box, Tooltip } from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import Button from "../../../common/inputs/Button";
import ConfirmDialog from "../../../common/feedback/ConfirmDialog";
import DeadlineCell, {
  URGENCY_COLOR,
} from "../../../common/display/DeadlineCell";
import StatusChip from "../../../common/display/StatusChip";
import StatChip from "../../../common/display/StatChip";
import ToggleChip from "../../../common/inputs/ToggleChip";
import InlineDatePicker from "../../../common/inputs/InlineDatePicker";
import InlineUserPicker from "../../../common/inputs/InlineUserPicker";
import Section from "../../../layout/Section";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import { hauptAktion, useEvidenceAktionen } from "./useEvidenceAktionen";
import { processingPathHeading } from "./processingPathTooltip";
import {
  DeadlineItem,
  EVIDENCE_STATUS_ORDER,
  Evidence,
  EvidenceFristfeldName,
  EvidenceUebergang,
  EvidenceUpdate,
} from "../../../../types";
import { OrgUser } from "../../../../services/api/users";
import { colors, fonts } from "../../../../styles/theme";
import { getEvidenceStatusVisual } from "../../../../utils/colors/evidenceStatusUtils";
import { Urgency, getUrgency } from "../../../../utils/urgency";
import {
  deletedDeadlinesNotice,
  manualDeadlinesOf,
  openManualDeadlines,
} from "../../../../utils/deadlines";
import { formatDate } from "../../../../utils/formatters";
import NoteIndicator from "../../../common/display/NoteIndicator";
import { ActionsMenuButton } from "../../../common/overlays/ActionsMenu";
import InfoTooltip from "../../../common/overlays/InfoTooltip";

const keineAktion = async (): Promise<Evidence | null> => null;

/** Fester Leerwert – ein `= []` als Default wäre bei jedem Rendern ein neues Array */
const NO_DEADLINES: DeadlineItem[] = [];

const rang = (evidence: Evidence): number =>
  EVIDENCE_STATUS_ORDER.indexOf(evidence.status);
const RANG_GELIEFERT = EVIDENCE_STATUS_ORDER.indexOf("Geliefert");

/**
 * Der Termin, den der jeweilige Status relevant macht.
 *
 * Offene Fristen (Angebotsfrist, Beauftragung, Lieferung, Prüfung) liefert der Server
 * (`offene_frist`) nach derselben Regel wie die Terminübersicht – eine neue Erinnerung erscheint
 * hier ohne Änderung. Ohne Frist steht das letzte Ereignis da, ohne Dringlichkeit: Sein Datum
 * liegt in der Vergangenheit, und als Frist gelesen stünde jede erledigte Zeile dauerhaft auf
 * überfällig. Ein archivierter Nachweis hat gar keinen Termin mehr.
 *
 * Manuelle Termine am Nachweis (ADR 010) verdrängen diesen Termin nicht – sie stehen als Hinweis
 * darunter (`ManualDeadlinesHint`).
 */
export const getTermin = (
  evidence: Evidence,
): {
  date: string | null;
  label: string;
  urgency: Urgency;
} => {
  if (evidence.archiviert) return { date: null, label: "", urgency: "normal" };
  if (evidence.offene_frist) {
    return {
      date: evidence.offene_frist.zieltermin,
      label: evidence.offene_frist.anlass,
      urgency: getUrgency(evidence.offene_frist.zieltermin),
    };
  }
  if (evidence.status === "Freigegeben" && evidence.freigegeben_am) {
    return {
      date: evidence.freigegeben_am,
      label: "Freigegeben am",
      urgency: "normal",
    };
  }
  if (evidence.status === "Erledigt" && evidence.completed_at) {
    return {
      date: evidence.completed_at,
      label: "Erledigt am",
      urgency: "normal",
    };
  }
  if (rang(evidence) >= RANG_GELIEFERT && evidence.geliefert_am) {
    return {
      date: evidence.geliefert_am,
      label: "Geliefert am",
      urgency: "normal",
    };
  }
  return { date: null, label: "", urgency: "normal" };
};

/**
 * Hinweis unter dem nächsten Termin, dass es am Nachweis noch manuelle Termine gibt: „+2 Termine“.
 * Im Tooltip stehen sie mit Datum, ein Klick öffnet sie im Detail. Statt eines weiteren Icons
 * trägt der Text die Dringlichkeit des frühesten – sonst ginge ein überfälliger Termin neben einer
 * entspannten Frist unter.
 */
const ManualDeadlinesHint: React.FC<{
  deadlines: DeadlineItem[];
  /** Ob darüber schon ein Termin steht – dann „+1 Termin“, sonst „1 Termin“ */
  additional: boolean;
  onOpen?: () => void;
}> = ({ deadlines, additional, onOpen }) => {
  const urgency = getUrgency(deadlines[0]?.zieltermin ?? undefined);
  const anzahl =
    deadlines.length === 1 ? "1 Termin" : `${deadlines.length} Termine`;
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {deadlines.map((deadline) => (
            <span key={deadline.id}>
              {formatDate(deadline.zieltermin ?? "")} · {deadline.bezeichnung}
            </span>
          ))}
        </Box>
      }
    >
      <Box
        component="span"
        role={onOpen ? "button" : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onClick={(event) => {
          if (!onOpen) return;
          // Sonst öffnete der Klick zusätzlich das Detail über die Zeile.
          event.stopPropagation();
          onOpen();
        }}
        onKeyDown={(event) => {
          if (onOpen && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            event.stopPropagation();
            onOpen();
          }
        }}
        sx={{
          color: urgency === "normal" ? "inherit" : URGENCY_COLOR[urgency],
          textDecoration: "underline dotted",
          textUnderlineOffset: "2px",
          cursor: onOpen ? "pointer" : "default",
        }}
      >
        {additional ? `+${anzahl}` : anzahl}
      </Box>
    </Tooltip>
  );
};

interface EvidencesSectionProps {
  evidences: Evidence[];
  orgUsers: OrgUser[];
  canEdit: boolean;
  onSelectEvidence: (evidenceId: string) => void;
  /** Speichert den Nachweis; meldet Erfolg und Fehler selbst über PageTemplate */
  onUpdate: (evidenceId: string, data: EvidenceUpdate) => Promise<void>;
  /**
   * Führt eine Aktion aus; `null`, wenn der Server sie abgelehnt hat. Ohne Angabe gibt es keine
   * Aktionen.
   */
  onTransition?: (
    evidenceId: string,
    data: EvidenceUebergang,
  ) => Promise<Evidence | null>;
  onDelete: (evidenceId: string) => Promise<void>;
  /** Titel der Kachel – die Nachweise-Seite teilt die Nachweise auf mehrere auf */
  title?: string;
  /**
   * Termine des Antrags – manuelle erscheinen als Hinweis unter „Nächster Termin" und zählen in
   * die Rückfrage beim Löschen
   */
  deadlines?: DeadlineItem[];
  /** Öffnet das Detail des Nachweises bei seinen Terminen – Klick auf den Hinweis „+1 Termin" */
  onOpenDeadlines?: (evidenceId: string) => void;
  /** Öffnet „Termin anlegen" mit dem Nachweis als Bezug */
  onAddDeadline?: (evidence: Evidence) => void;
  /** Zähler „x von y abgeschlossen" im Kopf – entfällt, wo die Seite ihn selbst zeigt */
  mitZaehler?: boolean;
  /**
   * Zeigt `evidences` vollständig, ohne archivierte auszublenden und ohne Archiv-Schalter –
   * für Kacheln, die schon nach Status vorsortiert sind.
   */
  vorsortiert?: boolean;
  /** Grundlage des Zählers „x von y abgeschlossen", wenn `evidences` nur ein Ausschnitt ist */
  alleEvidences?: Evidence[];
  /**
   * Von außen gewählter Nachweis (z. B. im Zeitstrahl): Die Zeile wird markiert und in den
   * sichtbaren Bereich geholt – das Muster von Karte und Tabelle. Öffnet nicht das Detail.
   */
  markierteEvidenceId?: string | null;
  /** Kostenposition je Nachweis und das Recht daran – für Plan- und Istkosten im Dialog */
  costs?: Parameters<typeof useEvidenceAktionen>[2];
}

// Die Breiten lassen die Aktionsspalte frei – zusammen ergeben sie 100 %. Andernfalls schöbe
// die von BaseTable ergänzte Spalte die Tabelle über die Seite hinaus.
const AKTIONEN_BREITE = "20%";

// Die Nachweisart steht als zweite Zeile unter der Bezeichnung statt in einer eigenen Spalte –
// einheitlich über `untertitel` an der Spalte (BaseTable).
const artUnterBezeichnung = (row: TableData) =>
  (row as Evidence).evidence_type?.name;

/** Was die Zeile für die Spalte „Nächster Termin" mitbringt */
interface TerminSpalte {
  naechster_termin: string | null;
  next_deadline_label: string;
  manual_deadlines: DeadlineItem[];
}

const columnsFor = (
  onOpenDeadlines?: (evidenceId: string) => void,
): TableColumn[] => [
  {
    key: "bezeichnung",
    label: "Bezeichnung",
    align: "left",
    // Die frei gewordene Breite der Spalten "Antrag" und "Benötigt in" geht an die Bezeichnung.
    width: "27%",
    sortable: true,
    untertitel: artUnterBezeichnung,
  },
  {
    key: "verantwortlich",
    label: "Verantwortlich",
    align: "center",
    width: "15%",
    sortable: true,
  },
  // Der Status steht vor dem Termin: Er entscheidet, welcher Termin daneben überhaupt
  // gezeigt wird (Lieferung, Erinnerung oder das Lieferdatum selbst).
  {
    key: "status",
    label: "Status",
    align: "center",
    width: "16%",
    sortable: true,
  },
  {
    key: "naechster_termin",
    label: "Nächster Termin",
    align: "center",
    width: "22%",
    sortable: true,
    // Das Datum oben, darunter grau, welcher Termin es ist ("Erinnerung am" …). Fehlt das
    // Datum noch, sagt der Anlass, welcher Termin hier gesetzt werden kann. Dahinter der Hinweis
    // auf manuelle Termine am Nachweis.
    untertitel: (row) => {
      const evidence = row as Evidence & TerminSpalte;
      const label = evidence.next_deadline_label || evidence.fristfeld?.anlass;
      if (evidence.manual_deadlines.length === 0) return label || null;
      return (
        <>
          {label && `${label} · `}
          <ManualDeadlinesHint
            deadlines={evidence.manual_deadlines}
            additional={!!evidence.naechster_termin}
            onOpen={
              onOpenDeadlines ? () => onOpenDeadlines(evidence.id) : undefined
            }
          />
        </>
      );
    },
  },
];

const EvidencesSection: React.FC<EvidencesSectionProps> = ({
  evidences,
  orgUsers,
  canEdit,
  onSelectEvidence,
  onUpdate,
  onTransition,
  onDelete,
  title = "Nachweise",
  mitZaehler = true,
  vorsortiert = false,
  alleEvidences,
  markierteEvidenceId,
  deadlines = NO_DEADLINES,
  onAddDeadline,
  onOpenDeadlines,
  costs,
}) => {
  const columns = useMemo(() => columnsFor(onOpenDeadlines), [onOpenDeadlines]);
  const [zeigeArchivierte, setZeigeArchivierte] = useState(false);
  const {
    oeffneMenu,
    oeffneAktion,
    elemente: aktionsElemente,
  } = useEvidenceAktionen(
    onTransition ?? keineAktion,
    (evidenceId) => manualDeadlinesOf(deadlines, { evidenceId }).length,
    costs,
  );
  const mitAktionen = canEdit && !!onTransition;

  // Gezählt wie im Antragsfortschritt: abgeschlossene unter den nicht archivierten (ADR 007).
  const benoetigt = (alleEvidences ?? evidences).filter((e) => !e.archiviert);
  const completedCount = benoetigt.filter((e) => e.completed).length;
  const archivierteCount = vorsortiert
    ? 0
    : evidences.filter((e) => e.archiviert).length;
  const sichtbareEvidences =
    vorsortiert || zeigeArchivierte
      ? evidences
      : evidences.filter((e) => !e.archiviert);

  const tableData = useMemo(
    () =>
      sichtbareEvidences.map((e) => {
        const termin = getTermin(e);
        return {
          ...e,
          art: e.evidence_type?.name ?? "–",
          naechster_termin: termin.date,
          next_deadline_label: termin.label,
          // Archiviert ruht alles – auch die manuellen Termine.
          manual_deadlines: e.archiviert
            ? []
            : openManualDeadlines(deadlines, e.id),
          verantwortlich: e.verantwortlich?.display_name ?? "",
        };
      }),
    [sichtbareEvidences, deadlines],
  );

  /** Die Benutzer zur Auswahl in der Spalte "Verantwortlich" */
  const userOptions = useMemo(
    () =>
      orgUsers.map((benutzer) => ({
        id: benutzer.user_id,
        name: benutzer.name || benutzer.email,
      })),
    [orgUsers],
  );

  const renderCell = useCallback(
    (column: TableColumn, row: TableData) => {
      const evidence = row as Evidence & {
        art: string;
        verantwortlich: string;
      };
      switch (column.key) {
        case "bezeichnung": {
          // Ab Geliefert gehört ein Dokument dazu; fehlt es, ist das ein Hinweis, kein Fehler.
          const ohneDokument =
            !evidence.archiviert &&
            rang(evidence) >= RANG_GELIEFERT &&
            evidence.dokumente.length === 0;
          return (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: fonts.weights.medium,
                    fontStyle: evidence.bezeichnung ? "normal" : "italic",
                    color:
                      evidence.bezeichnung && !evidence.archiviert
                        ? colors.text.primary
                        : colors.text.secondary,
                    textDecoration: evidence.archiviert
                      ? "line-through"
                      : "none",
                  }}
                >
                  {evidence.bezeichnung || "Ohne Bezeichnung"}
                </Typography>
                <NoteIndicator
                  note={evidence.note}
                  problem={evidence.problem}
                />
                {ohneDokument && (
                  <Tooltip title="Kein Dokument hinterlegt" arrow>
                    <LinkOffIcon
                      sx={{
                        fontSize: fonts.sizes.base,
                        color: colors.status.warning.main,
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        }
        case "verantwortlich": {
          // `tableData` hat das Feld bereits zum Anzeigenamen aufgelöst, damit die Spalte
          // sortierbar bleibt – hier steht eine Zeichenkette, kein User-Objekt.
          const name = (
            <Typography variant="caption" noWrap sx={{ display: "block" }}>
              {evidence.verantwortlich || "–"}
            </Typography>
          );
          // Ein Klick auf den Namen wechselt den Verantwortlichen direkt in der Zeile, ohne
          // das Detail zu öffnen.
          return (
            <InlineUserPicker
              value={evidence.verantwortlich_id}
              users={userOptions}
              editable={canEdit}
              ariaLabel={`Verantwortlichen ändern: ${evidence.verantwortlich || "keiner"}`}
              onChange={(userId) =>
                onUpdate(evidence.id, { verantwortlich_id: userId })
              }
            >
              {name}
            </InlineUserPicker>
          );
        }
        case "naechster_termin": {
          const termin = getTermin(evidence);
          // Änderbar ist nur die Frist des Status – laufende Nachweise. Offene haben keine,
          // freigegebene zeigen ihr Freigabedatum; das ändert man im Detail. Manuelle Termine
          // pflegt man in der Kachel „Termine" des Nachweises.
          const fristfeld = canEdit ? evidence.fristfeld : null;
          // Ohne Datum steht ein Strich da, solange der Status eine Frist kennt oder manuelle
          // Termine darunter stehen – sonst bliebe der Hinweis ohne Zeile darüber.
          const manuelle = (row as TableData)
            .manual_deadlines as DeadlineItem[];
          if (!termin.date && !evidence.fristfeld && manuelle.length === 0)
            return null;
          const zelle = (
            <DeadlineCell
              value={termin.date ?? undefined}
              urgency={termin.urgency}
            />
          );
          return (
            // Die Spalte ist zentriert; DeadlineCell ist ein Flex-Block und folgt text-align nicht.
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <InlineDatePicker
                value={fristfeld ? evidence[fristfeld.feld] : null}
                editable={!!fristfeld}
                ariaLabel={`${fristfeld?.anlass ?? "Termin"} ändern`}
                onChange={(wert) =>
                  fristfeld && onUpdate(evidence.id, { [fristfeld.feld]: wert })
                }
              >
                {zelle}
              </InlineDatePicker>
            </Box>
          );
        }
        case "status": {
          if (!evidence.status) return null;
          const chip = evidence.archiviert ? (
            <StatusChip
              label="Archiviert"
              color={colors.text.secondary}
              background={colors.background.tableHeader}
            />
          ) : (
            <StatusChip
              {...getEvidenceStatusVisual(evidence.status, evidence.completed)}
            />
          );
          // Reine Anzeige: Statuswechsel laufen über den Knopf und das Menü am Zeilenende,
          // dort auch „Bearbeitungsweg ändern“. Den Weg nennt der Tooltip am Status.
          const path = evidence.path_options.find(
            (o) => o.value === evidence.processing_path,
          );
          if (!path) return chip;
          return (
            // Ohne Hilfe-Cursor: Die Zeile ist anklickbar und behält ihren Zeiger.
            <InfoTooltip
              heading={processingPathHeading(path)}
              title={path.description}
              placement="top"
              showHelpCursor={false}
            >
              {chip}
            </InfoTooltip>
          );
        }
        default:
          return null;
      }
    },
    [canEdit, deadlines],
  );

  /** Nachweis, dessen Löschen gerade zu bestätigen ist (aus dem Zeilenmenü) */
  const [loeschKandidat, setLoeschKandidat] = useState<Evidence | null>(null);

  /**
   * Zeilenende: derselbe Knopf wie oben im Detail, daneben dasselbe Menü wie dort – hier
   * zusätzlich mit "Löschen", das sonst der Papierkorb wäre.
   */
  const renderActions = useCallback(
    (row: TableData) => {
      const evidence = row as Evidence;
      const aktion = hauptAktion(evidence);
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
          }}
          // Klicks hier führen Aktionen aus, öffnen nicht das Detail der Zeile.
          onClick={(event) => event.stopPropagation()}
        >
          {aktion && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => oeffneAktion(evidence, aktion)}
            >
              {aktion.label}
            </Button>
          )}
          <ActionsMenuButton
            ariaLabel={`Weitere Aktionen zu ${evidence.bezeichnung || evidence.evidence_type.name}`}
            onOpen={(anchor) =>
              oeffneMenu(evidence, anchor, {
                ohneHauptaktion: true,
                onLoeschen: () => setLoeschKandidat(evidence),
                onAddDeadline: onAddDeadline
                  ? () => onAddDeadline(evidence)
                  : undefined,
              })
            }
          />
        </Box>
      );
    },
    [oeffneAktion, oeffneMenu, onAddDeadline],
  );

  /**
   * Im Ausgangszustand stehen die offenen Nachweise oben, die freigegebenen und archivierten
   * darunter – getrennt durch eine Linie. Was noch zu tun ist, gehört nach vorn; was erledigt
   * ist, bleibt nachschlagbar. Sortiert der Anwender selbst, entfällt die Aufteilung.
   */
  const lieferungsBlock = useCallback((row: TableData) => {
    const evidence = row as Evidence;
    return evidence.completed || evidence.archiviert ? 1 : 0;
  }, []);

  /**
   * Die Rückfrage nennt die Bausteine, die den Nachweis benötigen, und die manuellen Termine,
   * die mitgelöscht werden – gesperrt wird nicht.
   */
  const deleteMessage = useCallback(
    (row: TableData) => {
      const evidence = row as Evidence;
      const name = evidence.bezeichnung || evidence.evidence_type.name;
      const bausteine = evidence.application_components;
      const termine = deletedDeadlinesNotice(
        manualDeadlinesOf(deadlines, { evidenceId: evidence.id }).length,
      );
      const liste = bausteine
        .map((ref) =>
          ref.elia_nummer ? `${ref.elia_nummer} ${ref.name}` : ref.name,
        )
        .join(", ");
      return [
        `Nachweis „${name}" wirklich löschen?`,
        bausteine.length > 0
          ? `Er wird benötigt in: ${liste}. Die Zuordnungen gehen dabei verloren.`
          : "",
        termine,
      ]
        .filter(Boolean)
        .join(" ");
    },
    [deadlines],
  );

  const handleDelete = useCallback(
    async (row: TableData) => {
      await onDelete(row.id);
    },
    [onDelete],
  );

  return (
    <Section
      title={title}
      noPadding
      noOuterPadding
      actions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {archivierteCount > 0 && (
            <ToggleChip
              value={zeigeArchivierte}
              activeLabel={`${archivierteCount} archiviert`}
              inactiveLabel={`${archivierteCount} archiviert`}
              onToggle={() => setZeigeArchivierte((prev) => !prev)}
            />
          )}
          {mitZaehler && (
            <StatChip
              value={`${completedCount}`}
              label={`von ${benoetigt.length} abgeschlossen`}
              backgroundColor={colors.brand.teal50}
              valueColor={colors.brand.teal600}
            />
          )}
        </Box>
      }
    >
      <BaseTable
        columns={columns}
        data={tableData}
        renderCell={renderCell}
        selectedRowId={markierteEvidenceId ?? undefined}
        onRowClick={(row) => onSelectEvidence(row.id)}
        // Mit Aktionen steht "Löschen" im Zeilenmenü statt als Papierkorb.
        onDelete={canEdit && !mitAktionen ? handleDelete : undefined}
        renderActions={mitAktionen ? renderActions : undefined}
        confirmDelete
        confirmDeleteMessage={deleteMessage}
        defaultGroupRank={lieferungsBlock}
        // Die Spalten teilen die verfügbare Breite nach festen Anteilen auf; `minWidth: 0`
        // nimmt BaseTable seine Mindestbreite von 650px.
        fixedLayout
        minWidth={0}
        actionsColumnWidth={AKTIONEN_BREITE}
        // Mittig statt rechtsbündig: Am rechten Rand liegt der senkrechte Bildlauf der
        // Tabelle über der Spalte.
        actionsColumnAlign="center"
        // Ohne Überschrift: Knopf und Menü sprechen für sich.
        actionsColumnLabel=""
        stickyHeader
        disablePagination
        defaultSortColumn="naechster_termin"
        defaultSortDirection="asc"
        emptyMessage="Noch keine Nachweise vorhanden"
      />

      {aktionsElemente}
      <ConfirmDialog
        open={!!loeschKandidat}
        title="Nachweis löschen"
        message={loeschKandidat ? deleteMessage(loeschKandidat) : ""}
        confirmLabel="Löschen"
        onConfirm={() => {
          if (loeschKandidat) handleDelete(loeschKandidat);
          setLoeschKandidat(null);
        }}
        onCancel={() => setLoeschKandidat(null)}
      />
    </Section>
  );
};

export default EvidencesSection;
